import { format, isValid, parse, parseISO } from "date-fns";

import { ApiReportSectionFieldsFieldTypeChoices } from "@api/graphql/types-and-hooks";

// Stored on the backend as a plain string (`BaseField.content`), same as every
// other field type. DATE_FORMAT matches Django's DEFAULT_DATE_FORMAT
// ("%m/%d/%Y", report_placeholder_service.py) byte-for-byte on purpose: that's
// the format `{dateOfReport}` already resolves to server-side before this
// field is ever read, so writing anything else (e.g. a bare ISO "yyyy-MM-dd")
// would round-trip through `new Date(str)` as UTC midnight while every known
// consumer (render-docxtemplater.ts, useTemplateExport.ts) formats it back out
// in local time — an off-by-one-day bug that `MM/dd/yyyy` doesn't have, since
// both sides treat it as local.
export const DATE_FORMAT = "MM/dd/yyyy";
export const DATETIME_DISPLAY_FORMAT = "MM/dd/yyyy hh:mm a";

// `signature_date` (PROA-2056) is routed onto this component while its
// backend field_type is still SIMPLE_TEXT, so existing rows hold whatever
// shape the old free-text field accepted, not necessarily DATE_FORMAT. Read
// all of them; only ever write DATE_FORMAT going forward.
const LEGACY_DATE_SHAPES: [RegExp, string][] = [
  [/^\d{1,2}\/\d{1,2}\/\d{4}$/, "M/d/yyyy"],
  [/^\d{4}-\d{1,2}-\d{1,2}$/, "yyyy-M-d"],
  [/^\d{1,2}-\d{1,2}-\d{4}$/, "M-d-yyyy"],
];

export const parseStoredDate = (value: string | null | undefined): Date | undefined => {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return undefined;
  for (const [shape, dateFormat] of LEGACY_DATE_SHAPES) {
    if (!shape.test(trimmed)) continue;
    const parsed = parse(trimmed, dateFormat, new Date());
    return isValid(parsed) ? parsed : undefined;
  }
  return parseStoredDatetime(trimmed);
};

export const parseStoredDatetime = (value: string | null | undefined): Date | undefined => {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return undefined;
  const parsed = parseISO(trimmed);
  return isValid(parsed) ? parsed : undefined;
};

export const formatStoredDate = (date: Date): string => format(date, DATE_FORMAT);

export const formatDisplayDate = (date: Date | undefined): string => (date ? format(date, DATE_FORMAT) : "");

export const formatDisplayDatetime = (date: Date | undefined): string =>
  date ? format(date, DATETIME_DISPLAY_FORMAT) : "";

/** Merges a newly-picked calendar day onto the time-of-day of an existing selection, defaulting to midnight. */
export const withTimeOfDay = (day: Date, previous: Date | undefined): Date => {
  const merged = new Date(day);
  merged.setHours(previous?.getHours() ?? 0, previous?.getMinutes() ?? 0, 0, 0);
  return merged;
};

/** Merges an `<input type="time">` value (`HH:mm`) onto the date-of-day of an existing selection. */
export const withTimeInputValue = (timeValue: string, previous: Date): Date | undefined => {
  const match = /^(\d{2}):(\d{2})$/.exec(timeValue);
  if (!match) return undefined;
  const merged = new Date(previous);
  merged.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return merged;
};

export const toTimeInputValue = (date: Date | undefined): string => (date ? format(date, "HH:mm") : "");

// Fields whose backend field_type is still SIMPLE_TEXT (their content is a
// date, but the template declares it as free text) get routed onto the
// calendar inputs by identifier instead (PROA-2056). This is the single
// source of truth for that override — consumed both by the registry (to pick
// a renderer) and by useReportForm (to validate the field's content as a
// date). Drop an identifier here once its ReportTemplateSectionField.field_type
// is migrated to DATE/DATETIME server-side.
export const DATE_FIELD_IDENTIFIER_OVERRIDES: Record<string, "date" | "datetime"> = {
  signature_date: "date",
  Date_of_Report: "date",
};

export const getDateFieldKind = (
  fieldType: ApiReportSectionFieldsFieldTypeChoices | string | null | undefined,
  fieldIdentifier: string | null | undefined,
): "date" | "datetime" | undefined => {
  const override = fieldIdentifier ? DATE_FIELD_IDENTIFIER_OVERRIDES[fieldIdentifier] : undefined;
  if (override) return override;
  if (fieldType === ApiReportSectionFieldsFieldTypeChoices.Date) return "date";
  if (fieldType === ApiReportSectionFieldsFieldTypeChoices.Datetime) return "datetime";
  return undefined;
};

// signature_date is seeded with the placeholder `{dateOfReport}`, which
// resolves server-side before the field is ever read for export — but not
// before a clinician who never touched the field opens it. Treated as valid
// here (not a real pickable day, so the calendar still shows it unselected),
// or a required+empty report would fail validation on every report that
// never touched the field.
const PLACEHOLDER_TOKEN = /^\{[A-Za-z]\w*\}$/;

/**
 * Whether a Date/Datetime field's current content is either empty, an
 * unresolved placeholder token, or a date our calendar inputs can parse. The
 * calendar itself can only ever write a good value — this exists for the
 * value that's already sitting in the field on load, seeded by the server (or
 * a pre-calendar free-text edit) before the user has touched it.
 * Required-ness is a separate check.
 */
export const isDateFieldContentValid = (
  fieldType: ApiReportSectionFieldsFieldTypeChoices | string | null | undefined,
  fieldIdentifier: string | null | undefined,
  content: string | null | undefined,
): boolean => {
  const kind = getDateFieldKind(fieldType, fieldIdentifier);
  if (!kind) return true;
  const trimmed = (content ?? "").trim();
  if (!trimmed || PLACEHOLDER_TOKEN.test(trimmed)) return true;
  return (kind === "datetime" ? parseStoredDatetime(trimmed) : parseStoredDate(trimmed)) !== undefined;
};

const INVALID_DATE_DISPLAY_LIMIT = 40;

/** Surfaces the unparseable value itself, so the clinician sees what's actually stored rather than a generic error. */
export const buildInvalidDateMessage = (content: string | null | undefined): string => {
  const trimmed = (content ?? "").trim();
  const shown =
    trimmed.length > INVALID_DATE_DISPLAY_LIMIT ? `${trimmed.slice(0, INVALID_DATE_DISPLAY_LIMIT)}…` : trimmed;
  return `This date couldn't be read (“${shown}”). Pick a new date to fix it.`;
};
